import { Button } from '@mui/material';
import {
  ConductEntriesRequest,
  GetConductEntriesQueryFilters,
  GetConductEntriesQuerySort,
  useExportConductForPage,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ExportStatus, PageExport } from '@schooly/components/export';
import { DELAY_BEFORE_HIDE_ICON_DONE } from '@schooly/constants';
import { format } from 'date-fns';
import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { downloadFile } from '../../utils/downloadFile';

export type ConductExportProps = {
  params: Omit<ConductEntriesRequest, 'filters' | 'sort'> & {
    filters: GetConductEntriesQueryFilters;
    sort?: GetConductEntriesQuerySort;
  };
  onSuccess?: () => void;
  onError?: () => void;
  conductLogsCount: number;
};

export const ConductExport: FC<ConductExportProps> = ({
  params,
  onSuccess,
  onError,
  conductLogsCount,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId } = useAuth();

  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);

  const { mutateAsync: exportConductForPage, isLoading } = useExportConductForPage();

  const resetExportStatus = useCallback(() => {
    setTimeout(() => setExportStatus(null), DELAY_BEFORE_HIDE_ICON_DONE);
  }, []);

  const handleError = useCallback(() => {
    onError?.();
    setExportStatus('error');
    resetExportStatus();
  }, [onError, resetExportStatus]);

  const handleSuccess = useCallback(() => {
    onSuccess?.();
    setExportStatus('success');
    resetExportStatus();
  }, [onSuccess, resetExportStatus]);

  const handleExportClick = useCallback(async () => {
    if (!schoolId) {
      return;
    }

    await exportConductForPage(params, {
      onSuccess: (data) => {
        try {
          const timestamp = format(new Date(), 'dd-MM-yy_HH-mm');
          downloadFile(data, `conducts_${timestamp}.csv`);

          handleSuccess();
        } catch (_error) {
          handleError();
        }
      },
      onError: handleError,
    });
  }, [schoolId, exportConductForPage, params, handleError, handleSuccess]);

  const renderMenuContent = useCallback(
    (close: () => void) => {
      return (
        <Button
          variant="outlined"
          onClick={() => {
            handleExportClick();
            close();
          }}
          disabled={!schoolId}
          fullWidth
        >
          {formatMessage({ id: 'conduct-Export' }, { count: conductLogsCount })}
        </Button>
      );
    },
    [schoolId, formatMessage, conductLogsCount, handleExportClick],
  );

  return (
    <PageExport
      isLoading={isLoading}
      exportStatus={exportStatus}
      renderMenuContent={renderMenuContent}
    />
  );
};
